import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, Save } from "lucide-react";

const defaultAgent = {
  name: "", niche: "", objective: "", tone_of_voice: "pastoral", content_type: "blog",
  structure_model: "", copy_style: "", target_audience: "", brand_voice_rules: "",
  forbidden_words: "", color_palette: "", typography_style: "", layout_style: "",
  mood: "", image_style: "", prompt_base: "", cta_model: "", hashtags_model: "",
  article_length: "medio", is_active: true,
};

const AdminAgentEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(defaultAgent);
  const [saving, setSaving] = useState(false);
  const isEdit = !!id;

  useEffect(() => {
    if (id) {
      supabase.from("agents").select("*").eq("id", id).single().then(({ data }) => {
        if (data) setForm(data as any);
      });
    }
  }, [id]);

  const set = (key: string, value: string | boolean) => setForm((p) => ({ ...p, [key]: value }));

  const save = async () => {
    if (!form.name || !form.niche) {
      toast({ title: "Nome e nicho são obrigatórios", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = { ...form };
    delete (payload as any).id;
    delete (payload as any).created_at;
    delete (payload as any).updated_at;

    let error;
    if (isEdit) {
      ({ error } = await supabase.from("agents").update(payload).eq("id", id));
    } else {
      ({ error } = await supabase.from("agents").insert(payload));
    }

    setSaving(false);
    if (error) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: isEdit ? "Agente atualizado!" : "Agente criado!" });
      navigate("/admin/agents");
    }
  };

  const renderField = (label: string, field: string, textarea?: boolean) => (
    <div className="space-y-1.5" key={field}>
      <Label className="text-sm">{label}</Label>
      {textarea ? (
        <Textarea value={(form as any)[field] || ""} onChange={(e) => set(field, e.target.value)} rows={3} />
      ) : (
        <Input value={(form as any)[field] || ""} onChange={(e) => set(field, e.target.value)} />
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/agents")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{isEdit ? "Editar Agente" : "Novo Agente"}</h1>
          <p className="text-sm text-muted-foreground">Configure a identidade persistente do agente</p>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["identity", "voice", "content", "visual", "prompt"]} className="space-y-3">
        <AccordionItem value="identity" className="rounded-xl border border-border bg-card px-5">
          <AccordionTrigger className="text-base font-semibold">Identidade</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            {renderField("Nome do Agente *", "name")}
            {renderField("Nicho *", "niche")}
            {renderField("Objetivo", "objective", true)}
            {renderField("Público-alvo", "target_audience")}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="voice" className="rounded-xl border border-border bg-card px-5">
          <AccordionTrigger className="text-base font-semibold">Voz e Estilo</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <div className="space-y-1.5">
              <Label className="text-sm">Tom de voz</Label>
              <Select value={form.tone_of_voice} onValueChange={(v) => set("tone_of_voice", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pastoral">Pastoral</SelectItem>
                  <SelectItem value="inspirador">Inspirador</SelectItem>
                  <SelectItem value="educativo">Educativo</SelectItem>
                  <SelectItem value="conversacional">Conversacional</SelectItem>
                  <SelectItem value="formal">Formal</SelectItem>
                  <SelectItem value="profético">Profético</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {renderField("Estilo de copy", "copy_style", true)}
            {renderField("Regras de marca", "brand_voice_rules", true)}
            {renderField("Palavras proibidas", "forbidden_words")}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="content" className="rounded-xl border border-border bg-card px-5">
          <AccordionTrigger className="text-base font-semibold">Conteúdo</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            {renderField("Modelo de estrutura", "structure_model", true)}
            <div className="space-y-1.5">
              <Label className="text-sm">Tamanho do artigo</Label>
              <Select value={form.article_length} onValueChange={(v) => set("article_length", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="curto">Curto (600-900 palavras)</SelectItem>
                  <SelectItem value="medio">Médio (1000-1500 palavras)</SelectItem>
                  <SelectItem value="longo">Longo (1800-2500 palavras)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {renderField("Modelo de CTA", "cta_model", true)}
            {renderField("Modelo de hashtags", "hashtags_model")}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="visual" className="rounded-xl border border-border bg-card px-5">
          <AccordionTrigger className="text-base font-semibold">Visual</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            {renderField("Paleta de cores", "color_palette")}
            {renderField("Estilo de tipografia", "typography_style")}
            {renderField("Estilo de layout", "layout_style")}
            {renderField("Mood", "mood")}
            {renderField("Estilo de imagem", "image_style", true)}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="prompt" className="rounded-xl border border-border bg-card px-5">
          <AccordionTrigger className="text-base font-semibold">Prompt Base</AccordionTrigger>
          <AccordionContent className="pb-5">
            {renderField("Prompt base customizado", "prompt_base", true)}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate("/admin/agents")}>Cancelar</Button>
        <Button onClick={save} disabled={saving}>
          <Save className="mr-1 h-4 w-4" /> {saving ? "Salvando..." : "Salvar Agente"}
        </Button>
      </div>
    </div>
  );
};

export default AdminAgentEditor;
